(function () {
  'use strict';

  function BookingPage(root) {
    this.root = root;
    this.apiBase = root.dataset.apiBase;
    this.form = root.querySelector('[data-booking-form]');
    this.message = root.querySelector('[data-booking-message]');
    this.dateInput = root.querySelector('[name="appointmentDate"]');
    this.typeSelect = root.querySelector('[name="appointmentType"]');
    this.timeInput = root.querySelector('[data-booking-time]');
    this.timesFieldset = root.querySelector('[data-booking-times]');
    this.timesHint = root.querySelector('[data-booking-times-hint]');
    this.slotsContainer = root.querySelector('[data-booking-slots]');
    this.submitButton = root.querySelector('[data-booking-submit]');
    this.success = root.querySelector('[data-booking-success]');
    this.slotRequest = null;
    this.slotRequestNumber = 0;
    this.submitting = false;
    this.submissionToken = createToken();
  }

  BookingPage.prototype.init = function () {
    this.dateInput.addEventListener('change', this.handleDateChange.bind(this));
    this.form.addEventListener('submit', this.handleSubmit.bind(this));
    this.loadSettings();
  };

  BookingPage.prototype.loadSettings = async function () {
    this.setMessage('Loading appointment settings…', false);
    try {
      var response = await this.fetchJson('?proxy_action=settings');
      if (!response.success || !response.settings) throw new Error(response.error || 'Unable to load booking settings.');

      var settings = response.settings;
      this.typeSelect.innerHTML = '<option value="">Select an appointment type</option>';
      settings.appointmentTypes.forEach(function (type) {
        var option = document.createElement('option');
        option.value = type;
        option.textContent = type;
        this.typeSelect.appendChild(option);
      }, this);
      this.dateInput.min = settings.minDate;
      this.dateInput.max = settings.maxDate;
      this.dateInput.disabled = false;
      this.submitButton.disabled = false;
      this.clearMessage();
    } catch (error) {
      this.setMessage(error.message || 'Unable to load booking settings. Please refresh and try again.', true);
    }
  };

  BookingPage.prototype.handleDateChange = function () {
    var dateIso = this.dateInput.value;
    this.clearSlots();
    if (!dateIso) return;

    if (this.slotRequest) this.slotRequest.abort();
    this.slotRequest = typeof AbortController === 'function' ? new AbortController() : null;
    var requestNumber = ++this.slotRequestNumber;
    this.timesFieldset.disabled = true;
    this.timesHint.textContent = 'Loading available appointment times…';
    this.setMessage('Checking availability…', false);

    this.fetchJson('?proxy_action=slots&date=' + encodeURIComponent(dateIso), {
      signal: this.slotRequest ? this.slotRequest.signal : undefined
    }).then(function (response) {
      if (requestNumber !== this.slotRequestNumber) return;
      if (!response.success) throw new Error(response.error || response.message || 'Unable to load appointment times.');
      this.renderSlots(response.slots || [], response.message || '');
      this.clearMessage();
    }.bind(this)).catch(function (error) {
      if (error.name === 'AbortError' || requestNumber !== this.slotRequestNumber) return;
      this.timesHint.textContent = 'Unable to load appointment times.';
      this.setMessage(error.message || 'Unable to load appointment times. Please try again.', true);
    }.bind(this));
  };

  BookingPage.prototype.renderSlots = function (slots, message) {
    this.slotsContainer.innerHTML = '';
    this.timesFieldset.disabled = slots.length === 0;
    this.timesHint.textContent = slots.length ? 'Choose an available time:' : (message || 'No appointment times are available on this date.');

    slots.forEach(function (time) {
      var button = document.createElement('button');
      button.type = 'button';
      button.className = 'booking-time';
      button.textContent = time;
      button.dataset.time = time;
      button.setAttribute('aria-pressed', 'false');
      button.addEventListener('click', this.selectTime.bind(this, button, time));
      this.slotsContainer.appendChild(button);
    }, this);
  };

  BookingPage.prototype.selectTime = function (button, time) {
    Array.prototype.forEach.call(this.slotsContainer.querySelectorAll('.booking-time'), function (item) {
      item.setAttribute('aria-pressed', item === button ? 'true' : 'false');
    });
    this.timeInput.value = time;
  };

  BookingPage.prototype.clearSlots = function () {
    this.timeInput.value = '';
    this.slotsContainer.innerHTML = '';
    this.timesFieldset.disabled = true;
    this.timesHint.textContent = 'Select a date to view available times.';
  };

  BookingPage.prototype.handleSubmit = async function (event) {
    event.preventDefault();
    if (this.submitting) return;
    if (!this.form.reportValidity()) return;
    if (!this.timeInput.value) {
      this.setMessage('Please select an available appointment time.', true);
      this.timesFieldset.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.submitting = true;
    this.submitButton.disabled = true;
    this.submitButton.textContent = 'Confirming…';
    this.setMessage('Confirming your appointment…', false);

    var data = {};
    new FormData(this.form).forEach(function (value, key) {
      data[key] = value;
    });
    data.submissionToken = this.submissionToken;

    try {
      var response = await this.fetchJson('?proxy_action=submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data)
      });
      if (!response.success) throw new Error(response.error || 'Unable to confirm the appointment.');
      this.showSuccess(response);
    } catch (error) {
      this.setMessage(error.message || 'Unable to confirm the appointment. Please try again.', true);
      this.submitting = false;
      this.submitButton.disabled = false;
      this.submitButton.textContent = 'Confirm Appointment';
    }
  };

  BookingPage.prototype.showSuccess = function (response) {
    this.form.hidden = true;
    this.clearMessage();
    this.success.querySelector('[data-success-date]').textContent = response.displayDate || formatDate(response.date);
    this.success.querySelector('[data-success-time]').textContent = response.time;
    this.success.querySelector('[data-success-reference]').textContent = response.appointmentId;
    if (response.emailSent === false) {
      this.success.querySelector('[data-success-email]').textContent = 'Your appointment is saved. Please keep the reference above; the confirmation email could not be sent.';
    }
    this.success.hidden = false;
    this.success.focus();
    this.success.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  BookingPage.prototype.fetchJson = async function (query, options) {
    var requestOptions = Object.assign({
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { Accept: 'application/json' }
    }, options || {});
    var response = await fetch(this.apiBase + query, requestOptions);
    var text = await response.text();
    var payload;
    try {
      payload = JSON.parse(text);
    } catch (error) {
      throw new Error('The booking service returned an invalid response.');
    }
    if (!response.ok) throw new Error(payload.error || 'The booking service is temporarily unavailable.');
    return payload;
  };

  BookingPage.prototype.setMessage = function (text, isError) {
    this.message.textContent = text;
    this.message.classList.toggle('is-error', Boolean(isError));
    this.message.hidden = false;
  };

  BookingPage.prototype.clearMessage = function () {
    this.message.textContent = '';
    this.message.classList.remove('is-error');
    this.message.hidden = true;
  };

  function createToken() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
    var random = window.crypto && window.crypto.getRandomValues ? window.crypto.getRandomValues(new Uint32Array(4)) : [Date.now(), Math.random() * 1e9, Date.now() / 2, Math.random() * 1e9];
    return Array.prototype.map.call(random, function (value) { return Math.floor(value).toString(16); }).join('-');
  }

  function formatDate(dateIso) {
    if (!dateIso) return '';
    return new Intl.DateTimeFormat('en-GB', {
      weekday: 'long', day: 'numeric', month: 'long', year: 'numeric'
    }).format(new Date(dateIso + 'T12:00:00'));
  }

  document.addEventListener('DOMContentLoaded', function () {
    document.querySelectorAll('[data-booking-root]').forEach(function (root) {
      new BookingPage(root).init();
    });
  });
})();
